import React, { useState, useEffect } from 'react';
import type { Node } from 'reactflow';
import type { FlowNodeData } from './types';
import { X } from 'lucide-react';

interface PropertiesPanelProps {
  selectedNode: Node<FlowNodeData> | null;
  onUpdateNode: (nodeId: string, data: Partial<FlowNodeData>) => void;
  onClose: () => void;
  departments: any[];
  templates: any[];
  readOnly?: boolean;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedNode,
  onUpdateNode,
  onClose,
  departments,
  templates,
  readOnly = false
}) => {
  const [label, setLabel] = useState('');
  const [config, setConfig] = useState<any>({});

  useEffect(() => {
    if (selectedNode) {
      setLabel(selectedNode.data.label || '');
      setConfig(selectedNode.data.config || {});
    }
  }, [selectedNode]);

  if (!selectedNode) {
    return (
      <div className="w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-6 flex items-center justify-center">
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Selecione um nó para configurar suas propriedades
        </p>
      </div>
    );
  }

  const handleConfigChange = (key: string, value: any) => {
    if (readOnly) return;
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    // Auto-save on change
    onUpdateNode(selectedNode.id, {
      label: label || selectedNode.data.label,
      config: newConfig
    });
  };

  const handleLabelChange = (newLabel: string) => {
    if (readOnly) return;
    setLabel(newLabel);
    onUpdateNode(selectedNode.id, {
      label: newLabel,
      config
    });
  };

  const renderConfigForm = () => {
    const nodeType = selectedNode.data.nodeType;

    switch (nodeType) {
      case 'SEND_MESSAGE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mensagem
              </label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                rows={5}
                placeholder="Digite a mensagem..."
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Use variáveis: {'{{'}customer_name{'}}'}, {'{{'}dias_cancelamento{'}}'}, etc.
              </p>
            </div>
          </div>
        );

      case 'SEND_TEMPLATE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Template
              </label>
              <select
                value={config.templateName || ''}
                onChange={(e) => handleConfigChange('templateName', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Selecione um template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Variáveis do Template (JSON)
              </label>
              <textarea
                value={config.templateVariables ? JSON.stringify(config.templateVariables, null, 2) : ''}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    handleConfigChange('templateVariables', parsed);
                  } catch (err) {
                    // Invalid JSON, don't update
                  }
                }}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                rows={4}
                placeholder='[{"key": "1", "value": "{{customer_name}}"}]'
              />
            </div>
          </div>
        );

      case 'TRANSFER_DEPARTMENT':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Departamento
              </label>
              <select
                value={config.departmentId || ''}
                onChange={(e) => {
                  const dept = departments.find(d => d.id === e.target.value);
                  handleConfigChange('departmentId', e.target.value);
                  if (dept) {
                    handleConfigChange('departmentName', dept.name);
                  }
                }}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Selecione um departamento</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="assignToAgent"
                checked={config.assignToAgent || false}
                onChange={(e) => handleConfigChange('assignToAgent', e.target.checked)}
                disabled={readOnly}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <label htmlFor="assignToAgent" className="text-sm text-gray-700 dark:text-gray-300">
                Atribuir automaticamente a um agente
              </label>
            </div>
          </div>
        );

      case 'UPDATE_STATUS':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Novo Status
            </label>
            <select
              value={config.status || ''}
              onChange={(e) => handleConfigChange('status', e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">Selecione um status</option>
              <option value="WAITING">Aguardando</option>
              <option value="MENU">Menu</option>
              <option value="QUEUED">Na Fila</option>
              <option value="ACTIVE">Ativo</option>
              <option value="ANSWERED">Respondida</option>
              <option value="CLOSED">Encerrada</option>
            </select>
          </div>
        );

      case 'CLOSE_CONVERSATION':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Motivo (opcional)
            </label>
            <input
              type="text"
              value={config.reason || ''}
              onChange={(e) => handleConfigChange('reason', e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder="Motivo do encerramento"
            />
          </div>
        );

      case 'SET_VARIABLE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nome da Variável
              </label>
              <input
                type="text"
                value={config.variableName || ''}
                onChange={(e) => handleConfigChange('variableName', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="nome_variavel"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Valor
              </label>
              <input
                type="text"
                value={config.value || ''}
                onChange={(e) => handleConfigChange('value', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Valor ou {{outra_variavel}}"
              />
            </div>
          </div>
        );

      case 'START':
      case 'WAIT_RESPONSE':
      case 'END':
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Este nó não requer configuração adicional.
          </p>
        );

      case 'CONDITION':
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            As condições são configuradas nas conexões (arestas) que saem deste nó.
            Conecte este nó a outros e configure as condições em cada conexão.
          </p>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">
          Propriedades {readOnly && <span className="text-xs font-normal text-yellow-600 dark:text-yellow-400">(Somente Leitura)</span>}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tipo do Nó
          </label>
          <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">
            {selectedNode.data.nodeType}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rótulo (opcional)
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => handleLabelChange(e.target.value)}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
            placeholder="Deixe vazio para usar o padrão"
          />
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Configuração</h4>
          {renderConfigForm()}
        </div>
      </div>
    </div>
  );
};
